import { FragmentDefiner, useReadFragment } from "../../../GlobalHooks/useReadFragment";
import { formatMoney, parserMoney, postRequest } from "../../../Utils/Functions";
import { useUser } from "../../User/User.hook";
import { useEffect, useState } from "react";
import { gql } from "@apollo/client/core";
import { useQuery } from "@apollo/client";

const QUERY_PRICE = gql`
	query property($id: ID) {
		property(id: $id) {
			price {
				amount
			}
		}
	}
`;

const BBVA_SERVICE = "https://prod.infocasas.com.uy/microservices/simulador/?/bbva/simulation";
const BBVA_GET_VALUES =
	"https://prod.infocasas.com.uy/microservices/simulador/?/bbva/getValuesProperty/";

export const useBBVAFinancing = ({ id, source, propertyPrice }) => {
	/* const { data: propertyData } = useQuery(QUERY_PRICE, { variables: { id } }); */ //Se comenta la línea de código ya que el padre trae la información del inmueble
	const { user } = useUser();
	const [loan, setLoan] = useState(0);
	const [income, setIncome] = useState(0);
	const [years, setYears] = useState(25);
	const [currency, setCurrency] = useState("ui");
	const [type, setType] = useState(0);
	const [asset, setAsset] = useState(0);
	const [payment, setPayment] = useState(0);
	const [loading, setLoading] = useState(true);
	const [firstTime, setFirstTime] = useState(true);
	const [error, setError] = useState(null);
	const [fixErrorObject, setFixErrorObject] = useState(null);
	const [extraData, setExtraData] = useState(null);
	const [show, setShow] = useState(true);
	
	let timer = null;

	const ASSETS = [
		{
			value: "sinnomina",
			text: "No",
		},
		{
			value: "nomina",
			text: "Si",
		},
	];

	const TYPES = [
		{
			value: "primeravivienda",
			text: "Vivienda Permanente",
		},
		{
			value: "segundavivienda",
			text: "Segunda Vivienda",
		},
	];

	const CURRENCIES = [
		{
			value: "ui",
			text: "Unidades Indexadas",
		},
		{
			value: "usd",
			text: "Dólares",
		},
	];

	const YEARS = [
		1,
		2,
		3,
		4,
		5,
		6,
		7,
		8,
		9,
		10,
		11,
		12,
		13,
		14,
		15,
		16,
		17,
		18,
		19,
		20,
		21,
		23,
		24,
		25,
	].map(year => ({
		value: year,
		text: `${year} año${year > 1 ? "s" : ""}`,
	}));

	const fixError = () => {
		if (fixErrorObject.monto) {
			setLoan(parserMoney(fixErrorObject.monto));
		}
		if (fixErrorObject.plazo) {
			setYears(fixErrorObject.plazo);
		}
		if (fixErrorObject.ingreso) {
			setIncome(parserMoney(fixErrorObject.ingreso));
		}
	};

	useEffect(() => {
		const getValues = async () => {
			try {
				const response = await fetch(BBVA_GET_VALUES + propertyPrice);
				const data = await response.json();

				if (data.error) {
					setShow(false);
					return;
				}

				if (data) {
					setLoan(parserMoney(data.montoasolicitar));
					setIncome(parserMoney(data.ingreso_mensual));
					setYears(data.plazo);
					setType(data.destino);
					setAsset(data.cliente);
					setPayment(data.cuota_por_defecto);
					setFirstTime(false);
				}
			} catch {
				return;
			}
		};

		if (propertyPrice > 0) {
			getValues();
		}
	}, [propertyPrice]);

	const bbvaService = async (dontStore = false) => {
		setLoading(true);
		const userId = user && user.data && user.data.me && user.data.me.id ? user.data.me.id : "";
		const data = await postRequest(BBVA_SERVICE + (dontStore ? `/${dontStore}` : ""), {
			valor_propiedad: formatMoney(propertyPrice),
			montopropiedad: `U$S ${formatMoney(propertyPrice)}`,
			IDpropiedad: id,
			IDusuario: userId,
			montoasolicitar: formatMoney(loan),
			ingreso_mensual: formatMoney(income),
			monedaprestamo: currency,
			plazo: years,
			destino: type,
			cliente: asset,
			source: source,
		});

		if (data) {
			setPayment(data.primer_cuota_con_gastos_uy);
			setExtraData(data);

			if (data.notice) {
				setError(data.notice);
				let errorObject = {
					monto: null,
					plazo: null,
					ingreso: null,
				};
				if (data.monto) {
					errorObject.monto = data.monto;
				}
				if (data.plazo) {
					errorObject.plazo = data.plazo;
				}
				if (data.ingreso) {
					errorObject.ingreso = data.ingreso;
				}
				setFixErrorObject(errorObject);
			} else {
				setError("");
			}
			setLoading(false);
			return data;
		}
	};

	useEffect(() => {
		if (!firstTime && loan > 0 && income > 0) {
			clearTimeout(timer);
			timer = setTimeout(() => {
				bbvaService(true);
			}, 600);
		}

		return () => clearTimeout(timer);
	}, [loan, income, years, currency, type, asset, firstTime]);

	return {
		loan: {
			amount: loan,
			change: setLoan,
		},
		property: {
			amount: propertyPrice,
		},
		income: {
			amount: income,
			change: setIncome,
		},
		years: {
			totalYears: YEARS,
			value: years,
			change: setYears,
		},
		currency: {
			options: CURRENCIES,
			value: currency,
			change: setCurrency,
		},
		type: {
			options: TYPES,
			value: type,
			change: setType,
		},
		asset: {
			options: ASSETS,
			value: asset,
			change: setAsset,
		},
		payment,
		error,
		fixError,
		extraData,
		show,
		bbvaService,
	};
};
